import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyIcon from '@mui/icons-material/Key';
import { useSnackbar } from 'notistack';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  listTokensRequest,
  revokeTokenRequest,
  regenerateTokenRequest,
  clearRevealedToken
} from '../../redux/actions/auth/apiTokens';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import ListingToolbar from '../common/ListingToolbar';
import DataTable from '../common/DataTable';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import { CMButton } from '../../containers/styledComponents';
import EditAPITokenDialog from './EditAPITokenDialog';
import TokenRevealDialog from './TokenRevealDialog';
import CodeSurface from './CodeSurface';
import { scopeLabel } from './apiTokenConstants';
import config from '../../config';

const SOON_THRESHOLD_DAYS = 7;

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const safeFormatDate = (v, fmt = 'MMM d, h:mm a', fb = 'Never') => {
  const date = parseDate(v);
  if (!date) return fb;
  try { return format(date, fmt); } catch (_e) { return fb; }
};

const safeRelative = (v) => {
  const date = parseDate(v);
  if (!date) return null;
  try { return `${formatDistanceToNow(date)} ago`; } catch (_e) { return null; }
};

const safeRelativeFuture = (v) => {
  const date = parseDate(v);
  if (!date) return null;
  try { return formatDistanceToNow(date, { addSuffix: true }); } catch (_e) { return null; }
};

// active | expiring | expired — drives the EntityCell tone stripe and
// the status filter chips.
const tokenStatus = (token) => {
  const expiresAt = parseDate(token.expires_at);
  if (token.status === 'expired' || (expiresAt && expiresAt < new Date())) {
    return 'expired';
  }
  if (expiresAt && expiresAt.getTime() - Date.now() < SOON_THRESHOLD_DAYS * 24 * 60 * 60 * 1000) {
    return 'expiring';
  }
  return 'active';
};

const STATUS_TONE = {
  active: 'success',
  expiring: 'warning',
  expired: 'error',
};

const STATUS_LABELS = {
  active: 'Active',
  expiring: 'Expires soon',
  expired: 'Expired',
};

const USAGE_SNIPPET = [
  `curl '${'<api-base-url>'}/workspaces' \\`,
  "  -H 'Authorization: Bearer pa_live_...'",
].join('\n');

// ``embedded`` renders the page inside the Settings shell (no
// breadcrumbs / intro caption — the shell owns the header).
const APITokensPage = ({ embedded = false }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tokenToEdit, setTokenToEdit] = useState(null);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [tokenToRegenerate, setTokenToRegenerate] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    tokens, isLoading, hasLoaded, error,
    isRevoking, isRegenerating, revealedToken
  } = useSelector((state) => state.auth.apiTokens);

  useEffect(() => {
    dispatch(listTokensRequest());
  }, [dispatch]);

  // Surface revoke success as a toast once the request settles.
  const wasRevokingRef = useRef(false);
  useEffect(() => {
    if (wasRevokingRef.current && !isRevoking && !error) {
      enqueueSnackbar('Token revoked', { variant: 'success' });
    }
    wasRevokingRef.current = isRevoking;
  }, [isRevoking, error, enqueueSnackbar]);

  useEffect(() => { setPage(0); }, [statusFilter, search]);

  // Note: tokenToRevoke/tokenToRegenerate are intentionally NOT nulled on
  // close, so the dialog text doesn't blank out during the close transition.
  const handleRevokeToken = (token) => {
    setTokenToRevoke(token);
    setRevokeDialogOpen(true);
  };

  const handleConfirmRevoke = () => {
    if (tokenToRevoke) {
      dispatch(revokeTokenRequest(tokenToRevoke.token_id));
      setRevokeDialogOpen(false);
    }
  };

  const handleEditToken = (token) => {
    setTokenToEdit(token);
    setEditDialogOpen(true);
  };

  const handleRegenerateToken = (token) => {
    setTokenToRegenerate(token);
    setRegenerateDialogOpen(true);
  };

  const handleConfirmRegenerate = () => {
    if (tokenToRegenerate) {
      dispatch(regenerateTokenRequest(tokenToRegenerate.token_id));
      setRegenerateDialogOpen(false);
    }
  };

  const handleRevealClose = () => {
    dispatch(clearRevealedToken());
    // Re-sync with the server after a create/regenerate settles.
    dispatch(listTokensRequest());
  };

  const toggleStatus = (s) =>
    setStatusFilter((prev) => prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]);

  const activeFilterCount = (search ? 1 : 0) + (statusFilter.length > 0 ? 1 : 0);

  const clearAll = () => {
    setSearch('');
    setStatusFilter([]);
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tokens || []).filter((t) => {
      if (statusFilter.length > 0 && !statusFilter.includes(tokenStatus(t))) return false;
      if (!q) return true;
      return (t.name || '').toLowerCase().includes(q)
        || (t.token_id || '').toLowerCase().includes(q)
        || (t.scopes || []).some((s) => s.toLowerCase().includes(q));
    });
  }, [tokens, search, statusFilter]);

  const pagedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (t) => {
        const s = tokenStatus(t);
        return (
          <EntityCell
            id={t.token_id}
            tone={STATUS_TONE[s]}
            label={STATUS_LABELS[s]}
          />
        );
      },
    },
    {
      key: 'name',
      label: 'Name',
      width: '24%',
      render: (t) => (
        <Typography variant="body2" sx={{
          fontWeight: 600, fontSize: 13,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: 320,
        }} title={t.name}>
          {t.name || '—'}
        </Typography>
      ),
    },
    {
      key: 'scopes',
      label: 'Scopes',
      width: '26%',
      render: (t) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 320 }}>
          {t.scopes?.length ? (
            t.scopes.map((scope) => (
              <Tooltip key={scope} title={scopeLabel(scope)} arrow placement="top">
                <Chip
                  label={scope}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Tooltip>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">No scopes</Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'last_used_at',
      label: 'Last Used',
      width: 140,
      render: (t) => (
        <Tooltip title={safeFormatDate(t.last_used_at, 'PPpp', 'Never used')} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {safeFormatDate(t.last_used_at, 'MMM d, h:mm a', 'Never')}
            </Typography>
            {t.last_used_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {safeRelative(t.last_used_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      width: 140,
      render: (t) => (
        <Tooltip title={safeFormatDate(t.created_at, 'PPpp', '—')} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {safeFormatDate(t.created_at, 'MMM d, h:mm a', '—')}
            </Typography>
            {t.created_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {safeRelative(t.created_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'expires_at',
      label: 'Expires',
      width: 140,
      render: (t) => (
        <Tooltip title={safeFormatDate(t.expires_at, 'PPpp', 'Never expires')} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {safeFormatDate(t.expires_at, 'MMM d, yyyy', 'Never')}
            </Typography>
            {t.expires_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {safeRelativeFuture(t.expires_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 60,
      align: 'right',
      stopRowClick: true,
      render: (t) => (
        <RowActions
          onOpen={null}
          items={[
            {
              label: 'Edit name and scopes',
              icon: EditOutlinedIcon,
              onClick: () => handleEditToken(t),
            },
            {
              label: 'Regenerate secret',
              icon: ReplayIcon,
              disabled: isRegenerating,
              onClick: () => handleRegenerateToken(t),
            },
            { divider: true },
            {
              label: 'Revoke token',
              icon: DeleteOutlineIcon,
              danger: true,
              disabled: isRevoking,
              onClick: () => handleRevokeToken(t),
            },
          ]}
        />
      ),
    },
  ];

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search by name, ID, scope…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 240, flex: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Status
        </Typography>
        {[
          { value: 'active',   label: 'Active',       color: 'success' },
          { value: 'expiring', label: 'Expires soon', color: 'warning' },
          { value: 'expired',  label: 'Expired',      color: 'error'   },
        ].map((opt) => {
          const selected = statusFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleStatus(opt.value)}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  const actionsNode = (
    <CMButton startIcon={<AddIcon />} onClick={() => navigate('/api-tokens/new')} size="small">
      New Token
    </CMButton>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {!embedded && (
        <PageBreadcrumbs items={[{ text: 'API Tokens', icon: <KeyIcon /> }]} mb={0.5} />
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: embedded ? 0 : 1 }}>
        Personal access tokens for programmatic access to the Predict.ai API. Secrets are shown once — keep them safe.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
        trailing={activeFilterCount > 0 ? `${filteredRows.length} of ${tokens.length}` : null}
      />

      <DataTable
        columns={columns}
        rows={pagedRows}
        getRowKey={(row) => row.token_id}
        loading={isLoading && !hasLoaded}
        error={hasLoaded && error && tokens.length === 0 ? error : null}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={filteredRows.length}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        emptyState={{
          icon: <KeyIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No tokens match your filters' : 'No API tokens yet',
          message: activeFilterCount > 0
            ? 'Try a different search or clear the filters.'
            : 'Create your first API token to start using the Predict.ai API programmatically.',
          action: activeFilterCount === 0 ? (
            <CMButton startIcon={<AddIcon />} onClick={() => navigate('/api-tokens/new')} size="small">
              Create Your First Token
            </CMButton>
          ) : null,
        }}
      />

      {/* Usage hint - same terminal surface as the deployment API guide */}
      <Box sx={{ mt: 3, maxWidth: 720 }}>
        <Typography
          variant="overline"
          sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1, mb: 1 }}
        >
          Your base URL
        </Typography>
        <CodeSurface
          label="API base URL"
          code={config.API_URL}
          onCopied={() => enqueueSnackbar('Base URL copied to clipboard', { variant: 'success' })}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', mb: 2.5 }}>
          This is where your account&apos;s data lives. The SDKs and API clients
          also accept the default <code>https://api.predict.ai</code> — requests
          are redirected here automatically — but using this URL directly skips
          that first hop.
        </Typography>
        <Typography
          variant="overline"
          sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1, mb: 1 }}
        >
          Using your token
        </Typography>
        <CodeSurface
          label="cURL"
          code={USAGE_SNIPPET.replace('<api-base-url>', config.API_URL)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Send the token as a Bearer credential in the Authorization header. It works anywhere your session does.
        </Typography>
      </Box>

      {/* Edit Token Dialog */}
      <EditAPITokenDialog
        open={editDialogOpen}
        token={tokenToEdit}
        onClose={() => {
          setEditDialogOpen(false);
          setTokenToEdit(null);
        }}
      />

      {/* One-time token reveal (create + regenerate) */}
      <TokenRevealDialog
        open={Boolean(revealedToken)}
        tokenPayload={revealedToken}
        onClose={handleRevealClose}
      />

      {/* Revoke Token Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
        <DialogTitle>Revoke API Token</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke the token "{tokenToRevoke?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Any applications using this token will stop working.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmRevoke}
            color="error"
            variant="contained"
            disabled={isRevoking}
          >
            {isRevoking ? 'Revoking...' : 'Revoke Token'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Token Dialog */}
      <Dialog open={regenerateDialogOpen} onClose={() => setRegenerateDialogOpen(false)}>
        <DialogTitle>Regenerate API Token</DialogTitle>
        <DialogContent>
          <Typography>
            Regenerate the secret for "{tokenToRegenerate?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The current secret stops working immediately and a new one will be
            shown once. The token keeps its ID, name, and scopes
            {tokenToRegenerate?.expires_at ? ', and its expiry clock restarts' : ''}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmRegenerate}
            color="primary"
            variant="contained"
            disabled={isRegenerating}
          >
            {isRegenerating ? 'Regenerating...' : 'Regenerate Token'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default APITokensPage;
